import { createContext, useContext, useState, useCallback, useRef } from 'react';
import type { ReactNode } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';

/**
 * Options for the confirm dialog
 */
export interface ConfirmOptions {
  /** Dialog title (optional) */
  title?: string;
  /** Message content - can be string or JSX */
  message: ReactNode;
  /** Text for the confirm button (default: "Confirm") */
  confirmText?: string;
  /** Text for the cancel button (default: "Cancel") */
  cancelText?: string;
  /** Visual variant - 'danger' shows red confirm button for destructive actions */
  variant?: 'default' | 'danger';
}

/**
 * Options for the alert dialog
 */
export interface AlertOptions {
  /** Dialog title (optional) */
  title?: string;
  /** Message content - can be string or JSX */
  message: ReactNode;
  /** Text for the OK button (default: "OK") */
  okText?: string;
}

interface DialogContextType {
  /** Show a confirmation dialog and return a Promise that resolves to true/false */
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  /** Show an alert dialog and return a Promise that resolves when dismissed */
  alert: (options: AlertOptions) => Promise<void>;
}

const DialogContext = createContext<DialogContextType | null>(null);

interface DialogState {
  isOpen: boolean;
  mode: 'confirm' | 'alert';
  title?: string;
  message: ReactNode;
  confirmText: string;
  cancelText: string;
  variant: 'default' | 'danger';
}

const initialState: DialogState = {
  isOpen: false,
  mode: 'confirm',
  message: '',
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  variant: 'default',
};

/**
 * DialogProvider
 *
 * Provides confirm() and alert() functions that return Promises.
 * This replaces native browser dialogs with styled, accessible modal dialogs.
 *
 * Usage:
 * ```tsx
 * const { confirm, alert } = useDialog();
 *
 * // Confirmation dialog
 * const confirmed = await confirm({
 *   title: 'Delete Item',
 *   message: 'Are you sure you want to delete this item?',
 *   variant: 'danger',
 * });
 * if (confirmed) {
 *   // proceed with deletion
 * }
 *
 * // Alert dialog (requires acknowledgment)
 * await alert({
 *   title: 'Password Created',
 *   message: <div>Your temporary password is: <strong>abc123</strong></div>,
 * });
 * ```
 */
export const DialogProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<DialogState>(initialState);

  // Store the resolve function for the current dialog
  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setState({
        isOpen: true,
        mode: 'confirm',
        title: options.title,
        message: options.message,
        confirmText: options.confirmText || 'Confirm',
        cancelText: options.cancelText || 'Cancel',
        variant: options.variant || 'default',
      });
    });
  }, []);

  const alert = useCallback((options: AlertOptions): Promise<void> => {
    return new Promise((resolve) => {
      resolveRef.current = () => resolve();
      setState({
        isOpen: true,
        mode: 'alert',
        title: options.title,
        message: options.message,
        confirmText: options.okText || 'OK',
        cancelText: '', // Not used in alert mode
        variant: 'default',
      });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    resolveRef.current?.(true);
    resolveRef.current = null;
    setState(initialState);
  }, []);

  const handleCancel = useCallback(() => {
    resolveRef.current?.(false);
    resolveRef.current = null;
    setState(initialState);
  }, []);

  return (
    <DialogContext.Provider value={{ confirm, alert }}>
      {children}
      <ConfirmDialog
        isOpen={state.isOpen}
        mode={state.mode}
        title={state.title}
        message={state.message}
        confirmText={state.confirmText}
        cancelText={state.cancelText}
        variant={state.variant}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </DialogContext.Provider>
  );
};

/**
 * Hook to access dialog functions
 *
 * @throws Error if used outside of DialogProvider
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useDialog = (): DialogContextType => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('useDialog must be used within DialogProvider');
  }
  return context;
};
